/**
 * PaymentPanel.js - P0-CORE: Componente único de pagamentos para serviços
 * 
 * Funcionalidades:
 * - Split payment (múltiplos métodos de pagamento)
 * - Botões rápidos por tipo (diagnóstico, adiantamento, mão de obra, saldo)
 * - Histórico de pagamentos
 * - Atualização automática após pagamento
 * - Impressão de comprovativo
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  CreditCard, Plus, Trash2, Receipt, Download, 
  DollarSign, CheckCircle, Clock, Printer
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import { servicesAPI, settingsAPI, printAPI } from '@/lib/api';

// Tipos de pagamento com labels
const PAYMENT_TYPES = {
  diagnostic: { label: 'Diagnóstico', color: 'bg-purple-100 text-purple-800' },
  partial: { label: 'Adiantamento', color: 'bg-blue-100 text-blue-800' },
  labour: { label: 'Mão de Obra', color: 'bg-orange-100 text-orange-800' },
  parts: { label: 'Peças', color: 'bg-cyan-100 text-cyan-800' },
  final: { label: 'Saldo Final', color: 'bg-green-100 text-green-800' }
};

export default function PaymentPanel({ 
  serviceId, 
  service, 
  onPaymentRegistered,
  quickPaymentType = null,  // 'diagnostic', 'labour', 'parts', 'final'
  suggestedAmount = null,
  disabled = false,
  compact = false
}) {
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentsData, setPaymentsData] = useState(null);
  
  // Split payment state - permite múltiplos métodos
  const [splitPayments, setSplitPayments] = useState([
    { payment_account_id: '', amount: '', reference: '' }
  ]);
  const [paymentType, setPaymentType] = useState(quickPaymentType || 'final');
  const [paymentNotes, setPaymentNotes] = useState('');

  // Load payment accounts
  useEffect(() => {
    const loadPaymentAccounts = async () => {
      try {
        const response = await settingsAPI.listActivePaymentAccounts();
        setPaymentAccounts(response.data || []);
      } catch (error) {
        console.error('Failed to load payment accounts:', error);
      }
    };
    loadPaymentAccounts();
  }, []);

  // Load payments data - reloads when service or its payments change
  useEffect(() => {
    const loadPayments = async () => {
      if (!serviceId) return;
      try {
        const response = await servicesAPI.getPayments(serviceId);
        setPaymentsData(response.data);
      } catch (error) {
        console.error('Failed to load payments:', error);
      }
    };
    loadPayments();
  }, [serviceId, service?.payments?.length, service?.updated_at]);

  // Set suggested amount when provided
  useEffect(() => {
    if (!suggestedAmount) return;
    setSplitPayments((current) => {
      if (current.length !== 1 || current[0].amount) {
        return current;
      }
      return [{ ...current[0], amount: suggestedAmount.toString() }];
    });
  }, [suggestedAmount]);

  // Add payment line (for split payment)
  const addPaymentLine = () => {
    setSplitPayments([...splitPayments, { payment_account_id: '', amount: '', reference: '' }]);
  };

  // Remove payment line
  const removePaymentLine = (index) => {
    if (splitPayments.length > 1) {
      setSplitPayments(splitPayments.filter((_, i) => i !== index));
    }
  };

  // Update payment line
  const updatePaymentLine = (index, field, value) => {
    const updated = [...splitPayments];
    updated[index] = { ...updated[index], [field]: value };
    setSplitPayments(updated);
  };

  // Calculate total being paid
  const totalBeingPaid = splitPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  // Register payment (supports split)
  const handleRegisterPayment = async () => {
    // Validate
    const validPayments = splitPayments.filter(p => p.payment_account_id && parseFloat(p.amount) > 0);
    
    if (validPayments.length === 0) {
      toast.error('Adicione pelo menos um pagamento com método e valor');
      return;
    }

    setLoading(true);
    try {
      if (paymentType === 'diagnostic') {
        if (validPayments.length > 1) {
          toast.error('O pagamento de diagnóstico aceita apenas um único método');
          setLoading(false);
          return;
        }

        const diagnosticPayment = validPayments[0];
        await servicesAPI.payDiagnostic(
          serviceId,
          diagnosticPayment.payment_account_id,
          diagnosticPayment.reference,
          paymentNotes,
          diagnosticPayment.payment_account_id
        );
      } else if (validPayments.length === 1) {
        // Pagamento único
        const p = validPayments[0];
        await servicesAPI.registerPayment(
          serviceId,
          parseFloat(p.amount),
          p.payment_account_id,
          paymentType,
          p.reference,
          paymentNotes,
          p.payment_account_id
        );
      } else {
        // Split payment
        const paymentsPayload = validPayments.map(p => ({
          payment_method_id: p.payment_account_id,
          payment_account_id: p.payment_account_id,
          amount: parseFloat(p.amount),
          reference: p.reference || null
        }));
        
        await servicesAPI.registerSplitPayment(
          serviceId,
          paymentsPayload,
          paymentType,
          paymentNotes
        );
      }

      toast.success(`Pagamento de ${formatCurrency(totalBeingPaid)} registado!`);
      
      // Reset form
      setSplitPayments([{ payment_account_id: '', amount: '', reference: '' }]);
      setPaymentNotes('');
      
      // Reload payments FIRST to update local state
      const response = await servicesAPI.getPayments(serviceId);
      setPaymentsData(response.data);
      
      // Then notify parent to refresh service data
      onPaymentRegistered?.();

    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registar pagamento');
    } finally {
      setLoading(false);
    }
  };

  // Print payment receipt - P0-TB2: Use authenticated PDF download
  const handlePrintReceipt = async (paymentId = null) => {
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'payment_receipt',
        service_id: serviceId,
        location_id: service?.location_id,
        module_group: 'SERVIÇOS',
        copy_type: 'Original',
        payment_id: paymentId
      }, 'preview');
      if (result.success) {
        if (result.usedFallback) {
          toast.warning('Template não configurado - usando modelo antigo', {
            description: result.warningMessage || 'Configure um template no Template Builder.',
            duration: 6000
          });
        } else {
          toast.success('Recibo aberto para impressão');
        }
      } else {
        toast.error(result.error || 'Erro ao imprimir comprovativo');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Erro ao imprimir comprovativo');
    }
  };

  // Quick payment buttons
  const handleQuickPayment = (type, amount) => {
    setPaymentType(type);
    setSplitPayments([{ payment_account_id: '', amount: amount.toString(), reference: '' }]);
  };

  // Compute summary from paymentsData or service
  const summary = paymentsData?.summary || {
    diagnostic_cost: service?.diagnostic_cost || 0,
    labour_cost: service?.quotation?.labour_cost || 0,
    parts_total: service?.quotation?.parts_total || 0,
    discount: service?.quotation?.discount || 0,
    vat_amount: service?.quotation?.vat_amount || 0,
    total: service?.quotation?.total || service?.diagnostic_cost || 0,
    total_paid: (service?.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0),
    balance: 0
  };
  
  // Calculate balance properly
  const computedTotal = summary.total !== undefined
    ? summary.total
    : (summary.diagnostic_cost || 0) + (summary.labour_cost || 0) + (summary.parts_total || 0) - (summary.discount || 0) + (summary.vat_amount || 0);
  const totalPaid = summary.total_paid || 0;
  const balance = summary.balance !== undefined ? summary.balance : (computedTotal - totalPaid);
  
  const payments = paymentsData?.payments || service?.payments || [];

  if (compact) {
    // Versão compacta para header - mostra resumo
    return (
      <div className="flex items-center gap-4 text-sm" data-testid="payment-summary-compact">
        <span>Total: <strong className="text-blue-600">{formatCurrency(computedTotal)}</strong></span>
        <Separator orientation="vertical" className="h-4" />
        <span>Pago: <strong className="text-green-600">{formatCurrency(totalPaid)}</strong></span>
        <Separator orientation="vertical" className="h-4" />
        <span>Saldo: <strong className={balance > 0 ? "text-amber-600" : "text-slate-500"}>{formatCurrency(balance)}</strong></span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payment-panel">
      {/* Summary Card */}
      <Card className="border-2 border-green-200 bg-green-50/50">
        <CardContent className="pt-4">
          <div className="grid grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-xs text-muted-foreground">Diagnóstico</p>
              <p className="text-lg font-bold">{formatCurrency(summary.diagnostic_cost || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Mão de Obra + Peças</p>
              <p className="text-lg font-bold">{formatCurrency((summary.labour_cost || 0) + (summary.parts_total || 0))}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">IVA</p>
              <p className="text-lg font-bold">{formatCurrency(summary.vat_amount || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Pago</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(summary.total_paid || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Saldo</p>
              <p className={cn(
                "text-lg font-bold",
                balance > 0 ? "text-red-600" : "text-green-600"
              )}>
                {formatCurrency(balance || 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Payment Buttons */}
      {!disabled && balance > 0 && (
        <div className="flex flex-wrap gap-2">
          {!paymentsData?.diagnostic_paid && summary.diagnostic_cost > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickPayment('diagnostic', summary.diagnostic_cost)}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="quick-pay-diagnostic"
            >
              <DollarSign className="h-3 w-3 mr-1" />
              Receber Diagnóstico ({formatCurrency(summary.diagnostic_cost)})
            </Button>
          )}
          {summary.labour_cost > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickPayment('labour', summary.labour_cost)}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="quick-pay-labour"
            >
              <DollarSign className="h-3 w-3 mr-1" />
              Receber Mão de Obra ({formatCurrency(summary.labour_cost)})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleQuickPayment('final', balance)}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="quick-pay-balance"
          >
            <DollarSign className="h-3 w-3 mr-1" />
            Receber Saldo ({formatCurrency(balance)})
          </Button>
        </div>
      )}

      {/* Payment Form */}
      {!disabled && balance > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Registar Pagamento
              </span>
              <Select value={paymentType} onValueChange={setPaymentType}>
                <SelectTrigger className="w-40 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PAYMENT_TYPES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} className="text-xs">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Payment Lines */}
            {splitPayments.map((payment, index) => (
              <div key={payment.id || `${payment.payment_account_id || payment.payment_type || 'payment'}-${payment.amount || '0'}-${index}`} className="flex items-center gap-2">
                <Select
                  value={payment.payment_account_id}
                  onValueChange={(v) => updatePaymentLine(index, 'payment_account_id', v)}
                >
                  <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`payment-method-${index}`}>
                    <SelectValue placeholder="Conta" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id} className="text-xs">
                        {account.name}{account.payment_method_name ? ` (${account.payment_method_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Valor"
                  className="w-28 h-8 text-xs"
                  value={payment.amount}
                  onChange={(e) => updatePaymentLine(index, 'amount', e.target.value)}
                  data-testid={`payment-amount-${index}`}
                />
                <Input
                  placeholder="Referência"
                  className="w-32 h-8 text-xs"
                  value={payment.reference}
                  onChange={(e) => updatePaymentLine(index, 'reference', e.target.value)}
                />
                {splitPayments.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-500"
                    onClick={() => removePaymentLine(index)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}

            {/* Add Split Payment Button */}
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-800"
              onClick={addPaymentLine}
              data-testid="add-split-payment"
            >
              <Plus className="h-3 w-3 mr-1" />
              Adicionar método (split payment)
            </Button>

            {/* Total & Submit */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="text-sm">
                Total a pagar: <strong className="text-green-600">{formatCurrency(totalBeingPaid)}</strong>
              </div>
              <Button
                onClick={handleRegisterPayment}
                disabled={loading || totalBeingPaid === 0}
                className="bg-green-600 hover:bg-green-700"
                data-testid="register-payment-btn"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Registar Pagamento
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              Histórico de Pagamentos ({payments.length})
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => handlePrintReceipt()}
              data-testid="print-all-receipts"
            >
              <Printer className="h-3 w-3 mr-1" />
              Recibo
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum pagamento registado
            </p>
          ) : (
            <div className="space-y-2">
              {payments.map((payment, idx) => (
                <div 
                  key={payment.id || idx} 
                  className="flex justify-between items-center p-2 bg-slate-50 rounded hover:bg-slate-100"
                  data-testid={`payment-history-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={cn("text-xs", PAYMENT_TYPES[payment.payment_type]?.color || 'bg-slate-100')}>
                          {PAYMENT_TYPES[payment.payment_type]?.label || payment.payment_type}
                        </Badge>
                        <span className="text-sm font-medium">{payment.payment_account_name || payment.payment_method_name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {payment.paid_at?.slice(0, 16).replace('T', ' ')}
                        {payment.reference && <span className="ml-2">Ref: {payment.reference}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-green-600">{formatCurrency(payment.amount)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handlePrintReceipt(payment.id)}
                      title="Imprimir comprovativo"
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}

              <Separator className="my-2" />

              <div className="flex justify-between font-bold pt-1">
                <span>Total Pago</span>
                <span className="text-green-600">{formatCurrency(summary.total_paid || 0)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fully Paid Message */}
      {balance <= 0 && balance !== undefined && (
        <div className="bg-green-100 border border-green-300 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800 font-medium">
            Serviço totalmente pago! Pode emitir recibo e guia de entrega.
          </span>
        </div>
      )}
    </div>
  );
}
